import React, { useState, useEffect } from 'react';
import { Trash2, Edit, Save, X, Users } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import './AdminPanel.css';

export enum UserRole {
  ADMIN = 'admin',
  EDITOR = 'editor',
  RELEASER = 'releaser',
  VIEWER = 'viewer',
  USER = 'user'
}

interface User {
  id: number;
  email: string;
  name: string;
  picture?: string;
  role: UserRole;
  is_active: boolean;
  created_at: string;
  last_login?: string;
}

const AdminPanel: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editingRole, setEditingRole] = useState<UserRole | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; user: User | null }>({
    isOpen: false,
    user: null
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users/');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: number) => {
    if (!editingRole) return;

    try {
      await api.put(`/users/${userId}/role`, null, {
        params: { role: editingRole }
      });
      toast.success('User role updated successfully');
      fetchUsers();
      setEditingUserId(null);
      setEditingRole(null);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update user role');
      console.error('Error updating user role:', error);
    }
  };

  const handleDeleteUser = (user: User) => {
    setDeleteConfirm({ isOpen: true, user });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.user) return;
    
    try {
      await api.delete(`/users/${deleteConfirm.user.id}`);
      toast.success('User deleted successfully');
      fetchUsers();
      setDeleteConfirm({ isOpen: false, user: null });
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
      console.error('Error deleting user:', error);
      setDeleteConfirm({ isOpen: false, user: null });
    }
  };

  const getRoleBadgeClass = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN:
        return 'badge badge-admin';
      case UserRole.EDITOR:
        return 'badge badge-editor';
      case UserRole.RELEASER:
        return 'badge badge-releaser';
      case UserRole.VIEWER:
        return 'badge badge-viewer';
      case UserRole.USER:
        return 'badge badge-user';
      default:
        return 'badge';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">
            <Users size={20} />
            사용자 관리
          </h2>
        </div>
        <div className="admin-card-content">
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>프로필</th>
                  <th>이메일</th>
                  <th>이름</th>
                  <th>권한</th>
                  <th>상태</th>
                  <th>가입일</th>
                  <th>마지막 로그인</th>
                  <th>작업</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      {user.picture ? (
                        <img
                          src={user.picture}
                          alt={user.name}
                          className="user-avatar-img"
                        />
                      ) : (
                        <div className="user-avatar-cell">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </td>
                    <td>{user.email}</td>
                    <td>{user.name}</td>
                    <td>
                      {editingUserId === user.id ? (
                        <select
                          className="role-select"
                          value={editingRole || user.role}
                          onChange={(e) => setEditingRole(e.target.value as UserRole)}
                        >
                          <option value={UserRole.ADMIN}>Admin</option>
                          <option value={UserRole.EDITOR}>Editor</option>
                          <option value={UserRole.RELEASER}>Releaser</option>
                          <option value={UserRole.VIEWER}>Viewer</option>
                          <option value={UserRole.USER}>User</option>
                        </select>
                      ) : (
                        <span className={getRoleBadgeClass(user.role)}>
                          {user.role.toUpperCase()}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={user.is_active ? 'badge badge-active' : 'badge badge-inactive'}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="text-sm">{formatDate(user.created_at)}</td>
                    <td className="text-sm">
                      {user.last_login ? formatDate(user.last_login) : 'Never'}
                    </td>
                    <td>
                      <div className="action-buttons">
                        {editingUserId === user.id ? (
                          <>
                            <button
                              className="btn-icon btn-save"
                              onClick={() => handleRoleChange(user.id)}
                              disabled={!editingRole || editingRole === user.role}
                            >
                              <Save size={16} />
                            </button>
                            <button
                              className="btn-icon btn-cancel"
                              onClick={() => {
                                setEditingUserId(null);
                                setEditingRole(null);
                              }}
                            >
                              <X size={16} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="btn-icon"
                              onClick={() => {
                                setEditingUserId(user.id);
                                setEditingRole(user.role);
                              }}
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              className="btn-icon btn-delete"
                              onClick={() => handleDeleteUser(user)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="사용자 삭제"
        message={`정말로 "${deleteConfirm.user?.email}" 사용자를 삭제하시겠습니까?\n\n이름: ${deleteConfirm.user?.name}\n권한: ${deleteConfirm.user?.role.toUpperCase()}`}
        confirmText="삭제"
        cancelText="취소"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, user: null })}
        variant="danger"
      />
    </div>
  );
};

export default AdminPanel;